import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  type HTMLAttributes,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setWarmTransferAlert } from '@/store/slices/callSlice';
import {
  openWarmTransferModal,
  resetWarmTransfer,
  selectIsWarmInProgress,
} from '@/store/slices/warmTransferSlice';
import { WarmTransferModal } from '@/components/contact-center/WarmTransferModal';
import { isChatContact } from '@/components/amazon-connect/contactUtils';
import {
  SupportAgent as SupportAgentIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { useAmazonConnectAgent } from '@/hooks/useAmazonConnectAgent';
import { useQueueTransferOptions } from '@/hooks/useQueueTransferOptions';
import { useAmazonConnectContacts } from '@/hooks/useAmazonConnectContacts';
import { connectAdapter } from '@/connect/adapter/streamsConnectAdapter';
import { callService } from '@/services/callService';
import { transferSseService } from '@/services/transferSse';
import {
  getInitialOrCaptureContactId,
  hasActiveCallLeg,
} from '@/components/amazon-connect/contactUtils';
import {
  connectionIdsBeforeDial,
  waitForThirdPartyConnectionConnected,
} from '@/utils/transferConnectionUtils';
import {
  filterTransferTargetsForChat,
  isTargetOnline,
} from '@/utils/transferTargetFilter';
import type { ConnectContact, ConnectQueueEndpoint } from '@/types';
import type {
  CombinedTransferTargetDTO,
  TransferResponseEvent,
  TransferTargetType,
} from '@/types/agents';
import { api } from '@/store/api';
import {
  Paper,
  Typography,
  Box,
  Button,
  Snackbar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DropdownField,
  type DropdownOption,
} from '@gapartners/ui';
type TransferTargetOption = {
  value: string;
  label: string;
  type: TransferTargetType;
  displayName: string;
  statusInfo?: string;
  status?: string;
  online?: boolean;
  targetPhoneNumber?: string;
  badgeColor:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning';
  badgeLabel: string;
};

type TransferTargetDropdownOption = TransferTargetOption & {
  id: string;
  primary: string;
  secondary?: string;
  searchText: string;
};

type QueueDropdownOption = {
  id: string;
  label: string;
  primary: string;
  searchText: string;
  secondary?: string;
  online?: boolean;
  endpoint?: ConnectQueueEndpoint;
  targetPhoneNumber?: string;
};

type TeamsTransferError = {
  data?: { message?: string };
  status?: number;
};

function hasCustomerConnection(contact: ConnectContact | undefined): boolean {
  if (!contact) return false;
  try {
    return contact.getConnections().some(connection => {
      try {
        const type = String(connection.getType?.() ?? '').toLowerCase();
        return type === 'inbound' || type === 'outbound';
      } catch {
        return false;
      }
    });
  } catch {
    return false;
  }
}

function hasActiveConsultConnection(
  contact: ConnectContact | undefined
): boolean {
  if (!contact) return false;
  // The customer leg (inbound or outbound) is the initial connection. Exclude it
  // so an outbound customer leg is never mistaken for a consult leg — both are
  // typed 'outbound' in Connect, only identity distinguishes them.
  let initialConnectionId: string | undefined;
  try {
    initialConnectionId = contact.getInitialConnection?.()?.getConnectionId?.();
  } catch {
    // Fall through: id stays undefined, matching on type only.
  }
  try {
    return contact.getConnections().some(connection => {
      try {
        const id = connection.getConnectionId?.();
        if (id && id === initialConnectionId) return false;
        const type = String(connection.getType?.() ?? '').toLowerCase();
        const isConsultLeg = type !== 'agent' && type !== 'inbound';
        if (!isConsultLeg) return false;
        return connection.isConnected?.() || connection.isOnHold?.();
      } catch {
        return false;
      }
    });
  } catch {
    return false;
  }
}

function splitOptionLabel(label: string): {
  primary: string;
  secondary?: string;
} {
  const [primary, ...rest] = label.split('\n');
  const secondary = rest.join('\n').trim();
  return { primary: primary.trim(), secondary: secondary || undefined };
}

function renderSearchableDropdownOption(
  props: HTMLAttributes<HTMLLIElement>,
  option: { id: string; primary: string; secondary?: string; online?: boolean }
) {
  return (
    <li {...props} key={option.id}>
      <Box
        sx={{
          py: 0.75,
          width: '100%',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {typeof option.online === 'boolean' && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: option.online ? 'success.main' : 'text.disabled',
                flexShrink: 0,
              }}
            />
          )}
          <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
            {option.primary}
          </Typography>
        </Box>
        {option.secondary ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.3, mt: 0.25 }}
          >
            {option.secondary}
          </Typography>
        ) : null}
      </Box>
    </li>
  );
}

const searchableDropdownSx = {
  '& .MuiAutocomplete-inputRoot': {
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  '& .MuiAutocomplete-input': {
    whiteSpace: 'normal',
    overflow: 'hidden',
    textOverflow: 'clip',
  },
};

/**
 * Dashboard panel for transferring an active call (e.g. to another queue or agent).
 * Shows dropdowns and actions when the agent has an active call; placeholder when not.
 * Extend with more dropdowns (e.g. transfer to agent) as needed.
 */
export function CallTransferPanel() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isWarmInProgress = useAppSelector(selectIsWarmInProgress);
  const [
    triggerGetCombinedTransferTargets,
    { data: transferTargetsData, isFetching: loadingTransferTargets },
  ] = api.useLazyGetCombinedTransferTargetsQuery();
  const [requestAgentTransfer] = api.useRequestAgentTransferMutation();
  const { agent } = useAmazonConnectAgent();
  const { activeContacts } = useAmazonConnectContacts();
  const contactId = useAppSelector(state => state.call.contactId);
  const warmTransferAlert = useAppSelector(
    state => state.call.warmTransferAlert
  );

  const activeContact = activeContacts.find(contact => {
    if (!hasActiveCallLeg(contact) || !contactId) return false;
    try {
      return (
        contact.getContactId() === contactId ||
        getInitialOrCaptureContactId(contact) === contactId
      );
    } catch {
      return false;
    }
  });
  const hasActiveCall = !!activeContact;
  const [connectionLegState, setConnectionLegState] = useState('');
  const [transferType, setTransferType] = useState<'cold' | 'warm'>('cold');
  const [coldModalOpen, setColdModalOpen] = useState(false);
  const [coldTargetKind, setColdTargetKind] = useState<'agent' | 'queue'>(
    'agent'
  );

  useEffect(() => {
    if (!activeContact) {
      setConnectionLegState('');
      return;
    }

    const getLegState = () => {
      try {
        return activeContact
          .getConnections()
          .map(connection => {
            try {
              return `${connection.getConnectionId?.() ?? ''}:${String(
                connection.getType?.() ?? ''
              ).toLowerCase()}:${connection.isConnected?.() ? '1' : '0'}:${
                connection.isOnHold?.() ? '1' : '0'
              }`;
            } catch {
              return 'unknown';
            }
          })
          .join('|');
      } catch {
        return '';
      }
    };

    const syncLegState = () => setConnectionLegState(getLegState());
    syncLegState();
    const intervalId = setInterval(syncLegState, 1000);
    return () => clearInterval(intervalId);
  }, [activeContact]);

  // Recompute block conditions whenever connection composition changes.
  // (Including when Agent 1 consult leg disappears after "Complete transfer".)
  void connectionLegState;
  const hasCustomerLeg = hasCustomerConnection(activeContact);
  const hasConsultLeg = hasActiveConsultConnection(activeContact);
  const shouldBlockWarmTransfer = !hasCustomerLeg || hasConsultLeg;
  const isChat = activeContact ? isChatContact(activeContact) : false;

  const { queueOptions, loadingQueues, refreshQueues } =
    useQueueTransferOptions(activeContact);

  // Reload the CCP quick connect list each time the cold transfer dialog opens.
  useEffect(() => {
    if (coldModalOpen) refreshQueues();
  }, [coldModalOpen, refreshQueues]);
  const [selectedQueueValue, setSelectedQueueValue] = useState<string>('');
  const [routingProfileName, setRoutingProfileName] = useState<string | null>(
    null
  );
  const [transferInProgress, setTransferInProgress] = useState(false);
  const [agentTransferInProgress, setAgentTransferInProgress] = useState(false);
  const [pendingTransferRequestId, setPendingTransferRequestId] = useState<
    string | null
  >(null);
  const [pendingTransferResponse, setPendingTransferResponse] =
    useState<TransferResponseEvent | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  const [selectedTransferTargetId, setSelectedTransferTargetId] =
    useState<string>('');
  const [transferTargetOptions, setTransferTargetOptions] = useState<
    TransferTargetOption[]
  >([]);
  const visibleTransferTargetOptions = useMemo(
    () => filterTransferTargetsForChat(transferTargetOptions, isChat),
    [transferTargetOptions, isChat]
  );
  // Agent/Teams dropdown: ESP agents + Teams users. Teams groups moved to queue dropdown.
  const agentTargetOptions = useMemo(
    () => visibleTransferTargetOptions.filter(o => o.type !== 'teams_group'),
    [visibleTransferTargetOptions]
  );
  const groupTargetOptions = useMemo(
    () => visibleTransferTargetOptions.filter(o => o.type === 'teams_group'),
    [visibleTransferTargetOptions]
  );
  const transferTriggerRef = useRef<'ui_click' | null>(null);
  const teamsTransferPollCleanupRef = useRef<(() => void) | null>(null);

  const clearTeamsTransferPoll = useCallback(() => {
    teamsTransferPollCleanupRef.current?.();
    teamsTransferPollCleanupRef.current = null;
  }, []);
  const canTransfer = useCallback(
    (
      requested: boolean,
      contact: ConnectContact | undefined
    ): contact is ConnectContact =>
      Boolean(hasActiveCall && contact && requested),
    [hasActiveCall]
  );

  const resetTransferState = useCallback(() => {
    clearTeamsTransferPoll();
    setPendingTransferRequestId(null);
    setPendingTransferResponse(null);
    setAgentTransferInProgress(false);
    setTransferInProgress(false);
    setSelectedQueueValue('');
    setSelectedTransferTargetId('');
    transferTriggerRef.current = null;
  }, [clearTeamsTransferPoll]);

  useEffect(() => () => clearTeamsTransferPoll(), [clearTeamsTransferPoll]);

  // Load routing profile name when agent is available
  useEffect(() => {
    if (!agent) {
      setRoutingProfileName(null);
      return;
    }
    try {
      const rp = agent.getRoutingProfile?.();
      setRoutingProfileName(rp?.name ?? null);
    } catch {
      setRoutingProfileName(null);
    }
  }, [agent]);

  const transferByPhone = (
    transferContact: ConnectContact,
    targetPhoneNumber: string
  ) => {
    const endpoint = connectAdapter
      .getCcpEnums()
      .byPhoneNumber(targetPhoneNumber);

    if (!endpoint) {
      setToast({
        message: t('components.amazonConnect.transferFailed'),
        type: 'error',
      });
      setAgentTransferInProgress(false);
      transferTriggerRef.current = null;
      return;
    }

    if (!transferContact.addConnection) {
      setToast({
        message: t('components.amazonConnect.transferFailed'),
        type: 'error',
      });
      setAgentTransferInProgress(false);
      transferTriggerRef.current = null;
      return;
    }

    const beforeConnectionIds = connectionIdsBeforeDial(transferContact);
    transferContact.addConnection(endpoint, {
      success: () => {
        setToast({
          message: t(
            'components.amazonConnect.transferInitiated',
            'Transferring call...'
          ),
          type: 'info',
        });

        const agentConnection = transferContact.getAgentConnection?.();
        if (!agentConnection) {
          setAgentTransferInProgress(false);
          transferTriggerRef.current = null;
          return;
        }

        const settleTransferFailure = (reason: string) => {
          console.error('Teams transfer failed before answer', { reason });
          setAgentTransferInProgress(false);
          transferTriggerRef.current = null;
          setToast({
            message: t('components.amazonConnect.transferFailed'),
            type: 'error',
          });
        };

        clearTeamsTransferPoll();
        teamsTransferPollCleanupRef.current =
          waitForThirdPartyConnectionConnected(
            transferContact,
            beforeConnectionIds,
            {
              legFilter: 'outbound',
              onConnected: () => {
                agentConnection.destroy?.({
                  success: () => {
                    callService.clearCall();
                    setAgentTransferInProgress(false);
                    transferTriggerRef.current = null;
                    setToast({
                      message: t('components.amazonConnect.transferSuccess'),
                      type: 'success',
                    });
                  },
                  failure: () => {
                    setAgentTransferInProgress(false);
                    transferTriggerRef.current = null;
                    setToast({
                      message: t('components.amazonConnect.transferFailed'),
                      type: 'error',
                    });
                  },
                });
              },
              onFailure: settleTransferFailure,
            }
          );
      },
      failure: () => {
        console.error('Failed to create transfer connection');
        clearTeamsTransferPoll();
        setAgentTransferInProgress(false);
        transferTriggerRef.current = null;
        setToast({
          message: t('components.amazonConnect.transferFailed'),
          type: 'error',
        });
      },
    });
  };

  const handleTransferToQueue = () => {
    const transferContact = activeContact;
    transferTriggerRef.current = 'ui_click';
    if (
      !canTransfer(true, transferContact) ||
      !selectedQueueValue ||
      transferInProgress ||
      agentTransferInProgress
    ) {
      transferTriggerRef.current = null;
      return;
    }
    const option = queueDropdownOptions.find(o => o.id === selectedQueueValue);
    if (!option) {
      transferTriggerRef.current = null;
      return;
    }
    if (option.targetPhoneNumber) {
      // Teams group destination — dial by phone (same as Teams user).
      setAgentTransferInProgress(true);
      transferByPhone(transferContact, option.targetPhoneNumber);
      return;
    }
    if (!option.endpoint) {
      transferTriggerRef.current = null;
      return;
    }
    setTransferInProgress(true);
    connectAdapter.transferToQueue(transferContact, option.endpoint, {
      success: () => {
        // Mirror disconnect cleanup after successful handoff.
        callService.clearCall();
        setTransferInProgress(false);
        setToast({
          message: t('components.amazonConnect.transferSuccess'),
          type: 'success',
        });
        transferTriggerRef.current = null;
      },
      failure: () => {
        setTransferInProgress(false);
        setToast({
          message: t('components.amazonConnect.transferFailed'),
          type: 'error',
        });
        transferTriggerRef.current = null;
      },
    });
  };

  /**
   * Handles dropdown open events to trigger data fetching.
   * Ensures transfer targets are loaded when user opens the dropdown.
   */
  const handleTransferTargetDropdownOpen = () => {
    void triggerGetCombinedTransferTargets(undefined, false);
  };

  const transferTargetDropdownOptions = useMemo<TransferTargetDropdownOption[]>(
    () =>
      agentTargetOptions.map(option => {
        const { primary, secondary } = splitOptionLabel(option.label);
        return {
          ...option,
          id: option.value,
          primary,
          secondary,
          searchText: option.label.replace(/\n/g, ' ').toLowerCase(),
        };
      }),
    [agentTargetOptions]
  );

  const selectedTransferTarget = useMemo(
    () =>
      transferTargetDropdownOptions.find(
        option => option.id === selectedTransferTargetId
      ) ?? null,
    [transferTargetDropdownOptions, selectedTransferTargetId]
  );

  const handleTransferTargetChange = (
    _event: unknown,
    value: DropdownOption | DropdownOption[] | null
  ) => {
    const selected = Array.isArray(value) ? value[0] : value;
    setSelectedTransferTargetId(selected ? String(selected.id) : '');
  };

  const queueDropdownOptions = useMemo<QueueDropdownOption[]>(() => {
    const queues: QueueDropdownOption[] = queueOptions.map(option => ({
      id: option.value,
      label: option.label,
      primary: option.label,
      searchText: option.label.toLowerCase(),
      endpoint: option.endpoint,
    }));
    const groups: QueueDropdownOption[] = groupTargetOptions.map(option => {
      const { primary, secondary } = splitOptionLabel(option.label);
      return {
        id: option.value,
        label: option.label,
        primary,
        secondary,
        searchText: option.label.replace(/\n/g, ' ').toLowerCase(),
        online: option.online,
        targetPhoneNumber: option.targetPhoneNumber,
      };
    });
    return [...queues, ...groups];
  }, [queueOptions, groupTargetOptions]);

  const selectedQueue = useMemo(
    () =>
      queueDropdownOptions.find(option => option.id === selectedQueueValue) ??
      null,
    [queueDropdownOptions, selectedQueueValue]
  );

  const handleQueueChange = (
    _event: unknown,
    value: DropdownOption | DropdownOption[] | null
  ) => {
    const selected = Array.isArray(value) ? value[0] : value;
    setSelectedQueueValue(selected ? String(selected.id) : '');
  };

  /**
   * Load transfer targets immediately when component mounts.
   * Do NOT wait for hasActiveCall - dropdown should have options ready before calls arrive.
   * Uses RTK Query caching to avoid redundant fetches within 30 second window.
   */
  useEffect(() => {
    if (!transferTargetsData && !loadingTransferTargets) {
      void triggerGetCombinedTransferTargets(undefined, false);
    }
  }, [
    triggerGetCombinedTransferTargets,
    transferTargetsData,
    loadingTransferTargets,
  ]);
  const getStatusInfo = (target: CombinedTransferTargetDTO) => {
    if (target.type === 'spog_agent') {
      return target.statusInfo
        ? `\n${t('components.contactCenter.sinceTime', { time: new Date(target.statusInfo).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })}`
        : '';
    }
    return '';
  };

  /**
   * Updates transfer target options when data is loaded.
   * Transforms API response into UI-friendly options with visual indicators.
   */
  useEffect(() => {
    if (transferTargetsData) {
      setTransferTargetOptions(
        transferTargetsData.items
          .map((target): TransferTargetOption => {
            const icon =
              target.type === 'spog_agent'
                ? '🎧'
                : target.type === 'teams_user'
                  ? '👤'
                  : '👥';
            return {
              value: target.id,
              label: `${icon} ${target.name}${getStatusInfo(target)}`,
              type: target.type,
              displayName: target.name,
              statusInfo: target.statusInfo,
              status: target.status,
              online: isTargetOnline(target),
              targetPhoneNumber: target.targetPhoneNumber,
              badgeColor:
                target.type === 'spog_agent'
                  ? 'primary'
                  : target.type === 'teams_user'
                    ? 'secondary'
                    : 'warning',
              badgeLabel:
                target.type === 'spog_agent'
                  ? 'SPOG'
                  : target.type === 'teams_user'
                    ? 'TEAMS'
                    : 'TEAMS GROUP',
            };
          })
          .sort((a, b) => Number(b.online === true) - Number(a.online === true))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferTargetsData]);

  /**
   * Resets all transfer state when call ends.
   * This ensures a clean slate when a new call arrives after a failed/declined transfer.
   * Clears:
   * - Selected transfer target
   * - Pending transfer request ID and response
   * - Transfer in progress flags
   */
  useEffect(() => {
    if (contactId === null) {
      setSelectedTransferTargetId('');
      resetTransferState();
      dispatch(resetWarmTransfer());
    }
  }, [contactId, resetTransferState, dispatch]);

  /**
   * Chat supports cold transfer only. Force cold and drop any selection
   * carried over from a voice session so a stale Teams/offline target
   * can never be transferred on a chat contact.
   */
  useEffect(() => {
    if (isChat) {
      setTransferType('cold');
      setSelectedTransferTargetId('');
      setColdTargetKind('agent');
    }
  }, [isChat]);

  /**
   * Also reset transfer state when active call ends (hasActiveCall becomes false).
   * This catches edge cases where Redux state might not update immediately.
   */
  useEffect(() => {
    if (!hasActiveCall) {
      setSelectedTransferTargetId('');
      resetTransferState();
    }
  }, [hasActiveCall, resetTransferState]);

  useEffect(() => {
    const unsubscribe = transferSseService.subscribe(event => {
      if (event.type !== 'transfer_response') return;
      if (event.mode === 'warm') return;
      console.log('[TransferDebug] Received transfer_response SSE event', {
        requestId: event.requestId,
        decision: event.decision,
        reason: event.reason,
        hasQuickConnect: !!event.quickConnect,
      });
      if (
        !pendingTransferRequestId ||
        event.requestId !== pendingTransferRequestId
      )
        return;
      setPendingTransferResponse(event);
    });
    return unsubscribe;
  }, [pendingTransferRequestId]);

  /**
   * Handles the transfer of an active call to either a Spog agent or Teams user.
   * Performs validation, determines transfer type, and executes the appropriate transfer logic.
   *
   * For Spog agents: Uses the existing agent transfer flow with SSE notifications
   * For Teams users: Looks up phone number via backend, then performs CCP cold transfer
   */
  const handleTransfer = async () => {
    const transferContact = activeContact;
    const transferContactId = transferContact
      ? connectAdapter.getCallScopeContactId(transferContact)
      : null;
    transferTriggerRef.current = 'ui_click';

    if (
      !selectedTransferTargetId ||
      loadingTransferTargets ||
      agentTransferInProgress
    ) {
      transferTriggerRef.current = null;
      return;
    }

    if (!transferContact || !hasActiveCall) {
      setToast({
        message: t(
          'components.contactCenter.transferAvailableDuringCall',
          'Transfer is available only after call pickup.'
        ),
        type: 'info',
      });
      transferTriggerRef.current = null;
      return;
    }

    const selectedTarget = agentTargetOptions.find(
      option => option.value === selectedTransferTargetId
    );
    if (!selectedTarget) {
      setToast({
        message: 'Selected transfer target not found.',
        type: 'error',
      });
      transferTriggerRef.current = null;
      return;
    }

    setAgentTransferInProgress(true);

    try {
      if (selectedTarget.type === 'spog_agent') {
        if (!transferContactId) {
          throw new Error('No active call ID available for SPOG transfer');
        }
        console.log('[TransferDebug] SPOG transfer request start', {
          targetId: selectedTransferTargetId,
          contactId: transferContactId,
        });
        // Handle Spog agent transfer using existing agent-to-agent flow
        const result = await requestAgentTransfer({
          toConnectId: selectedTransferTargetId,
          contactId: transferContactId,
        }).unwrap();
        console.log('[TransferDebug] SPOG transfer request created', {
          requestId: result.requestId,
        });
        setPendingTransferRequestId(result.requestId);
        setPendingTransferResponse(null);
        setToast({
          message: t(
            'components.contactCenter.transferRequestPending',
            'Waiting for agent response...'
          ),
          type: 'success',
        });
        // Don't set agentTransferInProgress to false here - it stays true until transfer completes
      } else {
        // Teams user destination — dial by phone.
        const targetPhoneNumber = selectedTarget.targetPhoneNumber;
        if (!targetPhoneNumber) {
          setToast({
            message: t('components.amazonConnect.transferFailed'),
            type: 'error',
          });
          setAgentTransferInProgress(false);
          transferTriggerRef.current = null;
          return;
        }
        transferByPhone(transferContact, targetPhoneNumber);
      }
    } catch (error: unknown) {
      // Handle all transfer errors here
      console.error('Transfer error:', error);
      let errorMessage: string = t(
        'components.contactCenter.transferRequestFailed',
        'Failed to transfer call.'
      );
      const typedError = error as TeamsTransferError;

      if (typedError?.data?.message) {
        errorMessage = typedError.data.message;
      } else if (typedError?.status === 401 || typedError?.status === 403) {
        errorMessage =
          'Your session has expired or you lack permissions. Please refresh the page and try again.';
      } else if (typedError?.status === 404) {
        errorMessage =
          'The Teams user was not found or does not have a business phone configured.';
      } else if (typedError?.status === 400) {
        errorMessage =
          typedError?.data?.message ||
          'Invalid request. Please check the Teams user and try again.';
      }

      setToast({
        message: errorMessage,
        type: 'error',
      });
      setAgentTransferInProgress(false);
      transferTriggerRef.current = null;
    }
  };

  useEffect(() => {
    if (!pendingTransferResponse || !activeContact) return;
    if (transferTriggerRef.current !== 'ui_click') {
      return;
    }
    if (!activeContact.isInbound()) {
      console.warn(
        '[TransferDebug] Ignoring transfer response: active contact is not inbound',
        { contactId: activeContact.getContactId() }
      );
      resetTransferState();
      return;
    }
    console.log('[TransferDebug] Processing pending transfer response', {
      requestId: pendingTransferResponse.requestId,
      decision: pendingTransferResponse.decision,
      reason: pendingTransferResponse.reason,
      hasQuickConnect: !!pendingTransferResponse.quickConnect,
      hasActiveContact: !!activeContact,
    });

    if (
      pendingTransferResponse.decision === 'accept' &&
      pendingTransferResponse.quickConnect
    ) {
      const endpointToUse = {
        endpoint: {
          endpointARN: pendingTransferResponse.quickConnect,
          endpointId: pendingTransferResponse.quickConnect,
          name: pendingTransferResponse.quickConnect,
          phoneNumber: null,
          queue: null,
          type: 'queue',
        },
      };

      connectAdapter.transferToQueue(activeContact, endpointToUse.endpoint, {
        success: () => {
          console.log('[TransferDebug] SPOG transferToQueue success', {
            requestId: pendingTransferResponse.requestId,
          });
          // Mirror disconnect cleanup after successful handoff.
          callService.clearCall();
          setToast({
            message: t('components.amazonConnect.transferSuccess'),
            type: 'success',
          });
          resetTransferState();
        },
        failure: () => {
          console.log('[TransferDebug] SPOG transferToQueue failure', {
            requestId: pendingTransferResponse.requestId,
          });
          setToast({
            message: t('components.amazonConnect.transferFailed'),
            type: 'error',
          });
          resetTransferState();
        },
      });
      return;
    }

    const reasonKeyMap: Record<string, string> = {
      declined: 'components.contactCenter.transferRequestDeclined',
      expired: 'components.contactCenter.transferRequestExpired',
      quick_connect_not_found:
        'components.contactCenter.transferToAgentQuickConnectMissing',
    };
    const reasonKey =
      reasonKeyMap[pendingTransferResponse.reason] ??
      'components.contactCenter.transferRequestFailed';
    setToast({
      message: t(reasonKey, 'Transfer request was not accepted.'),
      type: 'error',
    });
    resetTransferState();
  }, [pendingTransferResponse, activeContact, t, resetTransferState]);

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Typography
        variant="h6"
        fontWeight={700}
        color="text.primary"
        sx={{ mb: 2, fontSize: '1.125rem' }}
      >
        {t('components.contactCenter.transferOptionsTitle', 'Transfer options')}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button
          variant={transferType === 'cold' ? 'contained' : 'outlined'}
          color="primary"
          onClick={() => setTransferType('cold')}
        >
          {t('components.contactCenter.coldTransfer', 'Cold Transfer')}
        </Button>
        <Button
          variant={transferType === 'warm' ? 'contained' : 'outlined'}
          color="primary"
          onClick={() => setTransferType('warm')}
          disabled={isChat}
        >
          {t('components.contactCenter.warmTransfer', 'Warm Transfer')}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {transferType === 'cold'
          ? t('components.contactCenter.coldTransferDescription')
          : t('components.contactCenter.warmTransferDescription')}
      </Typography>

      {/* Transfer to queue */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {transferType === 'cold' && (
          <Box sx={{ pt: 1 }}>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={() => setColdModalOpen(true)}
              disabled={
                !hasActiveCall || agentTransferInProgress || isWarmInProgress
              }
            >
              {t(
                'components.contactCenter.coldTransferButton',
                'Cold transfer'
              )}
            </Button>
          </Box>
        )}

        {!hasActiveCall && (
          <Typography variant="body2" color="text.secondary">
            {t('components.contactCenter.transferAvailableDuringCall')}
          </Typography>
        )}

        {transferType === 'warm' && (
          <Box sx={{ pt: 1 }}>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={() => {
                if (shouldBlockWarmTransfer) return;
                dispatch(openWarmTransferModal());
              }}
              disabled={
                !hasActiveCall ||
                shouldBlockWarmTransfer ||
                isWarmInProgress ||
                agentTransferInProgress ||
                transferInProgress ||
                (activeContact ? isChatContact(activeContact) : false)
              }
            >
              {t(
                'components.contactCenter.warmTransferButton',
                'Warm transfer'
              )}
            </Button>
            {shouldBlockWarmTransfer && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.75 }}
              >
                {t(
                  'components.contactCenter.warmTransferNotCustomerCall',
                  'This is not a customer call — private consult while customer is on hold.'
                )}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <Dialog
        open={coldModalOpen}
        onClose={() => setColdModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('components.contactCenter.coldTransfer', 'Cold Transfer')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant={coldTargetKind === 'agent' ? 'contained' : 'outlined'}
                onClick={() => setColdTargetKind('agent')}
              >
                {t('components.contactCenter.transferToAgentOrTeams')}
              </Button>
              <Button
                size="small"
                variant={coldTargetKind === 'queue' ? 'contained' : 'outlined'}
                onClick={() => setColdTargetKind('queue')}
              >
                {t('components.amazonConnect.transferToQueue')}
              </Button>
            </Box>

            {coldTargetKind === 'agent' ? (
              <>
                <DropdownField
                  label={t('components.contactCenter.selectTransferTarget')}
                  labelString={t(
                    'components.contactCenter.selectTransferTarget'
                  )}
                  multiple={false}
                  options={transferTargetDropdownOptions}
                  value={selectedTransferTarget}
                  onChange={handleTransferTargetChange}
                  onOpen={handleTransferTargetDropdownOpen}
                  loading={loadingTransferTargets}
                  disabled={
                    !hasActiveCall ||
                    agentTransferInProgress ||
                    isWarmInProgress
                  }
                  getOptionId={option => option.id}
                  getOptionLabel={option =>
                    (option as TransferTargetDropdownOption).displayName
                  }
                  filterOption={(option, inputValue) =>
                    (
                      option as TransferTargetDropdownOption
                    ).searchText.includes(inputValue.toLowerCase())
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  sx={searchableDropdownSx}
                  textFieldProps={{
                    placeholder: t(
                      'components.contactCenter.searchTransferTarget'
                    ),
                  }}
                  renderOption={(props, option) =>
                    renderSearchableDropdownOption(
                      props,
                      option as TransferTargetDropdownOption
                    )
                  }
                  fullWidth
                />
                {selectedTransferTargetId && (
                  <Chip
                    icon={
                      transferTargetOptions.find(
                        o => o.value === selectedTransferTargetId
                      )?.type === 'spog_agent' ? (
                        <SupportAgentIcon />
                      ) : (
                        <GroupsIcon />
                      )
                    }
                    label={
                      transferTargetOptions.find(
                        o => o.value === selectedTransferTargetId
                      )?.badgeLabel || ''
                    }
                    color={
                      transferTargetOptions.find(
                        o => o.value === selectedTransferTargetId
                      )?.badgeColor || 'default'
                    }
                    size="small"
                    sx={{ minWidth: 60 }}
                  />
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block' }}
                >
                  {t('components.contactCenter.transferTargetLegend')}
                </Typography>
              </>
            ) : (
              <>
                {routingProfileName &&
                  queueDropdownOptions.length === 0 &&
                  !loadingQueues && (
                    <Typography variant="body2" color="text.secondary">
                      {t('components.contactCenter.noQueueQuickConnects')}
                    </Typography>
                  )}
                <DropdownField
                  label={t('components.amazonConnect.selectQueue')}
                  labelString={t('components.amazonConnect.selectQueue')}
                  multiple={false}
                  options={queueDropdownOptions}
                  value={selectedQueue}
                  onChange={handleQueueChange}
                  loading={loadingQueues}
                  disabled={
                    !hasActiveCall ||
                    loadingQueues ||
                    transferInProgress ||
                    isWarmInProgress
                  }
                  getOptionId={option => option.id}
                  getOptionLabel={option => option.label}
                  filterOption={(option, inputValue) =>
                    (option as QueueDropdownOption).searchText.includes(
                      inputValue.toLowerCase()
                    )
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  sx={searchableDropdownSx}
                  textFieldProps={{
                    placeholder: t('components.amazonConnect.searchQueue'),
                  }}
                  renderOption={(props, option) =>
                    renderSearchableDropdownOption(
                      props,
                      option as QueueDropdownOption
                    )
                  }
                  fullWidth
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setColdModalOpen(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (coldTargetKind === 'agent') {
                void handleTransfer();
              } else {
                handleTransferToQueue();
              }
              setColdModalOpen(false);
            }}
            disabled={
              coldTargetKind === 'agent'
                ? !hasActiveCall ||
                  loadingTransferTargets ||
                  agentTransferInProgress ||
                  isWarmInProgress ||
                  !selectedTransferTargetId ||
                  visibleTransferTargetOptions.length === 0 ||
                  !!pendingTransferRequestId
                : !hasActiveCall ||
                  loadingQueues ||
                  transferInProgress ||
                  agentTransferInProgress ||
                  isWarmInProgress ||
                  !selectedQueueValue ||
                  queueDropdownOptions.length === 0
            }
          >
            {agentTransferInProgress || transferInProgress
              ? t('common.loading')
              : t('components.amazonConnect.transfer')}
          </Button>
        </DialogActions>
      </Dialog>

      <WarmTransferModal
        activeContact={activeContact}
        hasActiveCall={hasActiveCall}
      />

      <Snackbar
        open={!!toast}
        onClose={() => setToast(null)}
        message={toast?.message}
        variant="toast"
        severity={toast?.type ?? 'info'}
        autoHideDuration={3000}
      />
      <Snackbar
        open={!!warmTransferAlert}
        onClose={() => dispatch(setWarmTransferAlert(null))}
        message={
          warmTransferAlert
            ? t(
                `components.contactCenter.warmTransferError.${warmTransferAlert}`,
                warmTransferAlert
              )
            : ''
        }
        variant="toast"
        severity="error"
        autoHideDuration={8000}
      />
    </Paper>
  );
}
